import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Empty, Tag, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import styles from './CourseStructure.less';

const { Text } = Typography;

const STATUS_PRESENTATION = {
  completed: {
    label: '已完成',
    className: 'completed',
  },
  default_completed: {
    label: '未完成',
    className: 'notCompleted',
  },
  skipped: {
    label: '已跳过',
    className: 'skipped',
  },
  not_completed: {
    label: '未完成',
    className: 'notCompleted',
  },
};

function actionName(instance, actionNames) {
  return (
    instance.definition?.actionName ||
    instance.actionName ||
    actionNames[instance.actionId] ||
    '动作 ' + (instance.actionId || '-')
  );
}

function planText(instance) {
  const parts = [];
  if (instance.plannedGroups) parts.push(instance.plannedGroups + ' 组');
  if (instance.plannedRepeatTimes) {
    parts.push(instance.plannedRepeatTimes + ' 次');
  }
  if (instance.plannedDurationSeconds) {
    parts.push(instance.plannedDurationSeconds + ' 秒');
  }
  return parts.join(' · ') || '已记录';
}

function completionBasisText(instance) {
  if (instance.completionBasis === 'xplan_visionkit') return '完成依据：VK';
  if (instance.completionBasis === 'xplan_video') return '完成依据：训练视频';
  return null;
}

function groupByPhase(items) {
  const grouped = new Map();
  items.forEach((item) => {
    const key = String(item.phaseId || item.phaseOrder || 'unknown');
    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        phaseId: item.phaseId,
        phaseName: item.phaseName || '训练阶段',
        phaseOrder: item.phaseOrder || 0,
        priority: item.trainingPhasePriority || 0,
        actions: [],
      });
    }
    grouped.get(key).actions.push(item);
  });
  return [...grouped.values()]
    .sort(
      (left, right) =>
        left.phaseOrder - right.phaseOrder || left.priority - right.priority,
    )
    .map((phase) => ({
      ...phase,
      actions: phase.actions.sort(
        (left, right) =>
          (left.actionSeqPriority || 0) - (right.actionSeqPriority || 0),
      ),
    }));
}

export default function CourseStructure({
  actionInstances,
  actionNames,
  onOpenAction,
}) {
  const phases = useMemo(
    () => groupByPhase(actionInstances || []),
    [actionInstances],
  );
  const counts = useMemo(
    () =>
      (actionInstances || []).reduce((result, item) => {
        const key =
          item.completionStatus === 'completed' ||
          item.completionStatus === 'skipped'
            ? item.completionStatus
            : 'not_completed';
        result[key] = (result[key] || 0) + 1;
        return result;
      }, {}),
    [actionInstances],
  );

  return (
    <Card bordered={false} className={styles.card}>
      <div className={styles.header}>
        <div>
          <h3>本节课训练结构</h3>
          <Text type="secondary">
            按训练阶段还原动作执行路径，共 {actionInstances.length} 个动作实例
          </Text>
        </div>
        <div className={styles.legend}>
          <span className={styles.completed}>
            已完成 {counts.completed || 0}
          </span>
          <span className={styles.skipped}>跳过 {counts.skipped || 0}</span>
          <span className={styles.notCompleted}>
            未完成 {counts.not_completed || 0}
          </span>
        </div>
      </div>

      {phases.length === 0 ? (
        <Empty description="暂无课程动作结构" />
      ) : (
        <div className={styles.timeline}>
          {phases.map((phase, phaseIndex) => (
            <section className={styles.phase} key={phase.key}>
              <div className={styles.phaseRail}>
                <span className={styles.phaseIndex}>{phaseIndex + 1}</span>
                {phaseIndex < phases.length - 1 && (
                  <span className={styles.phaseLine} />
                )}
              </div>
              <div className={styles.phaseContent}>
                <div className={styles.phaseHeader}>
                  <div>
                    <Text strong>{phase.phaseName}</Text>
                    <Text type="secondary">{phase.actions.length} 个动作</Text>
                  </div>
                  {phase.phaseOrder > 0 && <Tag>阶段 {phase.phaseOrder}</Tag>}
                </div>
                <div className={styles.actionGrid}>
                  {phase.actions.map((instance, actionIndex) => {
                    const status =
                      STATUS_PRESENTATION[instance.completionStatus] ||
                      STATUS_PRESENTATION.not_completed;
                    const canOpen = Boolean(instance.evaluationId);
                    const completionBasis = completionBasisText(instance);
                    return (
                      <article
                        className={
                          styles.actionTile + ' ' + styles[status.className]
                        }
                        key={
                          instance.instanceId ||
                          [
                            instance.phaseId,
                            instance.actionId,
                            instance.actionSeqId,
                          ].join(':')
                        }
                      >
                        <div className={styles.actionTop}>
                          <span className={styles.actionOrder}>
                            {actionIndex + 1}
                          </span>
                          <span className={styles.statusLabel}>
                            {status.label}
                          </span>
                        </div>
                        <Text
                          strong
                          ellipsis={{
                            tooltip: actionName(instance, actionNames),
                          }}
                          className={styles.actionName}
                        >
                          {actionName(instance, actionNames)}
                        </Text>
                        <Text type="secondary" className={styles.plan}>
                          {planText(instance)}
                        </Text>
                        {completionBasis && (
                          <Text className={styles.completionBasis}>
                            {completionBasis}
                          </Text>
                        )}
                        <div className={styles.actionFooter}>
                          <Text type="secondary">
                            ID {instance.actionId || '-'}
                          </Text>
                          {canOpen ? (
                            <Button
                              type="link"
                              size="small"
                              icon={<EyeOutlined />}
                              onClick={() => onOpenAction(instance)}
                            >
                              查看质量
                            </Button>
                          ) : (
                            <Text type="secondary">未采集动作质量</Text>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </section>
          ))}
        </div>
      )}
    </Card>
  );
}

CourseStructure.propTypes = {
  actionInstances: PropTypes.arrayOf(PropTypes.shape({})),
  actionNames: PropTypes.objectOf(PropTypes.string),
  onOpenAction: PropTypes.func.isRequired,
};

CourseStructure.defaultProps = {
  actionInstances: [],
  actionNames: {},
};
